import React from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { FaExternalLinkAlt, FaReceipt } from "react-icons/fa";
import Price from "../../pageInside/productsPrice";
import ShowimagesLoading from "../../pageInside/showImagesLoading";
import { OrderStatusBadge, PaymentStatusBadge } from "./OrderStatusBadge";
import { elakayAutoText } from "../../pageInside/dataElakay";


// Same query-string convention already used marketplace-wide to carry a selected variant into
// /product/preview/:id (pageInside/CardCartAvailable.js's urlFilter()) -- adapted only to skip a
// bare trailing "?" when there is nothing to carry, and to read the Order line item's own
// selectedFilters/filter snapshot fields (CartItemSchemaNew) rather than a populated FilterProduct.
const buildVariantQuery = (item) => {
  const filter = item?.products?.selectedFilters && Object.keys(item.products.selectedFilters).length > 0
    ? item.products.selectedFilters
    : item?.products?.filter;
  if (!filter || typeof filter !== "object" || Object.keys(filter).length === 0) return "";
  const params = Object.entries(filter)
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  return params.length > 0 ? `?${params.join("&")}` : "";
};

const productHref = (item) => {
  const productId = item?.products?.productID;
  if (!productId) return null;
  return `/product/preview/${productId}${buildVariantQuery(item)}`;
};

// Prefers the real matched variant name/value the list aggregation already resolves
// (prod_filterProduct, e.g. {name:"Color", value:"Red"}); falls back to the line item's own
// plain variant label. Never renders "undefined"/"null"/"[object Object]" -- returns null instead
// so the card simply omits the row.
const variantLabel = (item) => {
  const matched = item?.prod_filterProduct;
  if (matched?.value && matched?.name) return `${matched.name}: ${matched.value}`;
  if (matched?.value) return matched.value;
  if (item?.products?.variant) return String(item.products.variant);
  return null;
};

const quantityOf = (item) => Number(item?.products?.qty || item?.products?.count || 1);

const ProductRow = ({ item, onViewProduct }) => {
  const href = productHref(item);
  const name = item?.prod_name || "Product";
  const variant = variantLabel(item);
  const qty = quantityOf(item);

  const image = item?.products?.urlImg ? (
    <ShowimagesLoading src={item.products.urlImg} width={90} alt={name} />
  ) : (
    <div className="ph-no-image">{elakayAutoText("h_fc5e5bdcd5f6e6", "No image")}</div>
  );

  return (
    <div className="ph-product-row">
      {href ? (
        <Link to={href} className="ph-product-image" onClick={() => onViewProduct?.(item)}>
          {image}
        </Link>
      ) : (
        <div className="ph-product-image">{image}</div>
      )}

      <div className="ph-product-info">
        {href ? (
          <Link to={href} className="ph-product-name" onClick={() => onViewProduct?.(item)}>
            {name}
          </Link>
        ) : (
          <span className="ph-product-name">{name}</span>
        )}
        {variant && <div className="ph-product-variant">{variant}</div>}
        <div className="ph-product-qty">{elakayAutoText("purchaseQuantity", "Qty: {value0}", { value0: qty })}</div>
      </div>

      <div className="ph-product-price">
        <Price showAddToCartButton={false} products={item.products} />
      </div>
    </div>
  );
};

const PurchaseCard = ({ order, onViewProduct, onOpenOrder }) => {
  const { orders_id, createdAt, amountTotal, items } = order;
  const primaryItem = items[0];
  const orderRef = String(orders_id || "").slice(-8).toUpperCase();
  const orderedOn = createdAt ? moment(createdAt).format("MMM D, YYYY") : null;

  return (
    <div className="ph-card">
      <div className="ph-card-header">
        <div className="ph-card-header-left">
          <span className="ph-order-ref"><FaReceipt aria-hidden="true" />{elakayAutoText("purchaseOrderReference", "ORDER #{value0}", { value0: orderRef })}</span>
          {orderedOn && <span className="ph-order-date">{elakayAutoText("purchaseOrderedOn", "Ordered {value0}", { value0: orderedOn })}</span>}
        </div>
        <div className="ph-card-header-badges">
          <PaymentStatusBadge status={primaryItem?.products?.payment?.status} />
          <OrderStatusBadge status={primaryItem?.products?.status} />
        </div>
      </div>

      {items.length === 1 ? (
        <ProductRow item={primaryItem} onViewProduct={onViewProduct} />
      ) : (
        <div className="ph-multi-item">
          <div className="ph-multi-item-thumbs">
            {items.slice(0, 3).map((item, index) => {
              const href = productHref(item);
              const thumb = item?.products?.urlImg ? (
                <ShowimagesLoading src={item.products.urlImg} width={56} alt={item?.prod_name || elakayAutoText("h_dd3b86d1ef21b9", "Product")} />
              ) : (
                <div className="ph-no-image ph-no-image--small">{elakayAutoText("h_fc5e5bdcd5f6e6", "No image")}</div>
              );
              const key = item?.products?._id || index;
              return href ? (
                <Link
                  key={key}
                  to={href}
                  className="ph-multi-item-thumb"
                  onClick={() => onViewProduct?.(item)}
                  title={item?.prod_name || elakayAutoText("h_dd3b86d1ef21b9", "Product")}
                >
                  {thumb}
                </Link>
              ) : (
                <div key={key} className="ph-multi-item-thumb" title={item?.prod_name || elakayAutoText("h_dd3b86d1ef21b9", "Product")}>
                  {thumb}
                </div>
              );
            })}
          </div>
          <div className="ph-multi-item-info">
            <div className="ph-multi-item-count">{elakayAutoText("purchaseItemsCount", "{value0} items", { value0: items.length })}</div>
            <div className="ph-multi-item-names">
              {items.slice(0, 3).map((item) => item?.prod_name).filter(Boolean).join(", ")}
              {items.length > 3 ? `, +${items.length - 3} more` : ""}
            </div>
          </div>
        </div>
      )}

      <div className="ph-card-actions">
        {items.length === 1 && productHref(primaryItem) && (
          <Link
            to={productHref(primaryItem)}
            className="ph-btn ph-btn-outline"
            onClick={() => onViewProduct?.(primaryItem)}
          >
            <FaExternalLinkAlt aria-hidden="true" />{elakayAutoText("h_97dc6a99ba5fae", "View Product")}</Link>
        )}
        <button type="button" className="ph-btn ph-btn-primary" onClick={() => onOpenOrder?.(primaryItem)}>{elakayAutoText("h_abb713a37cc9ba", "Order Details")}</button>
      </div>

      {typeof amountTotal === "number" && (
        <div className="ph-card-total">
          <span>{elakayAutoText("h_3e821b1c63d1b2", "Order Total")}</span>
          <strong>${Number(amountTotal).toFixed(2)}</strong>
        </div>
      )}
    </div>
  );
};

export default PurchaseCard;
